import TuneIcon from '@mui/icons-material/Tune';
import { Box, Divider, styled } from '@mui/material';
import { DataGrid, DataGridProps, gridClasses, GridColDef } from '@mui/x-data-grid';
import React, { useMemo } from 'react';

import WsColors from '../../WsColors';
import { PopoverOnClick } from '../PopoverOnClick';
import { FieldOption, WsFieldSelector } from '../WsFieldSelector';
import { WsPaginationBar } from '../wsPaginationBar';

const StyledDataGrid = styled(DataGrid)(() => ({
  backgroundColor: WsColors.White,
  border: 'none',
  '& .MuiCheckbox-root': {
    color: WsColors.GreyBlack,
  },
  '& .Mui-checked': {
    color: WsColors.TheBlues500,
  },
  '& .MuiDataGrid-columnHeaders': {
    fontWeight: 800,
  },
  [`& .${gridClasses.cell}`]: {
    p: 1,
  },
  '& .MuiDataGrid-viewport': {
    overflow: 'auto',
  },
  '& .MuiDataGrid-footerContainer': {
    marginRight: 3,
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  '& .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontSize: '16px',
    fontWeight: 'bold',
  },
  '& .MuiDataGrid-columnHeader:focus': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeader:active': {
    outline: 'none',
  },
}));

export interface IWsDataGridProps extends DataGridProps {
  columnVisibility?: boolean;
  columnSettings?: FieldOption[];
  onColumnSettingsChange?: (options: FieldOption[]) => void;
  onColumnSettingsReset?: () => void;
  totalRows?: number;
}

const WsDataGrid = ({
  columns,
  rows,
  page = 0,
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
  totalRows,
  onColumnSettingsChange,
  onColumnSettingsReset,
  columnSettings,
  columnVisibility,
  ...restProps
}: IWsDataGridProps) => {
  const totalRecords = totalRows ?? rows.length;

  const handlePageChange = (pageNo: number) => onPageChange?.(pageNo, {} as any);
  const handlePageSizeChange = (newPageSize: number) => onPageSizeChange?.(newPageSize, {} as any);

  const processedColumnSettings = useMemo(() => {
    if (!columnVisibility || !columns) return null;

    // Step 1: Remove elements from columnSettings not present in columns
    const columnFields = columns.map((col) => col.field);
    const filteredSettings = columnSettings
      ? columnSettings
          .filter((setting) => columnFields.includes(setting.id))
          .map((setting) => ({
            ...setting,
            label: columns.find((col) => col.field == setting.id)?.headerName || setting.id,
          }))
      : [];

    // Step 2: Add elements from columns not present in filteredSettings
    const filteredSettingIds = new Set(filteredSettings.map((setting) => setting.id));
    const missingColumns: FieldOption[] = columns
      .filter((col) => !filteredSettingIds.has(col.field))
      .map((col) => ({
        id: col.field,
        label: col.headerName || col.field,
        checked: true,
      }));

    return [...filteredSettings, ...missingColumns];
  }, [columnVisibility, columns, columnSettings]);

  const settingsMenuColumn = useMemo(
    (): GridColDef => ({
      field: 'column-settings-qwertyui',
      sortable: false,
      width: 40,
      disableColumnMenu: true,
      renderHeader: () => (
        <PopoverOnClick
          element={
            <Box display="flex" justifyContent="center" alignItems="center" width="100%">
              <TuneIcon />
            </Box>
          }
        >
          <WsFieldSelector
            key={JSON.stringify(processedColumnSettings)}
            searchable
            initialOptions={processedColumnSettings || []}
            onApply={(options) => onColumnSettingsChange?.(options)}
            onReset={() => onColumnSettingsReset?.()}
          />
        </PopoverOnClick>
      ),
    }),
    [processedColumnSettings, onColumnSettingsChange, onColumnSettingsReset]
  );

  const columnsData: GridColDef[] = useMemo(() => {
    if (!columnVisibility || !processedColumnSettings) return columns;

    const visibleColumns = processedColumnSettings
      .filter((setting) => setting.checked)
      .map((setting) => columns.find((col) => col.field == setting.id))
      .filter((x) => !!x);

    // Put columnMenu into 'action' column header if present, else into end
    const actionIdx = visibleColumns.findIndex(
      (col) => col.field == 'action' || col.field == 'edit'
    );

    let finalColumns: GridColDef[];
    if (actionIdx !== -1) {
      const actionCol = visibleColumns[actionIdx];
      finalColumns = [
        ...visibleColumns.slice(0, actionIdx),
        {
          ...actionCol,
          sortable: settingsMenuColumn.sortable,
          disableColumnMenu: settingsMenuColumn.disableColumnMenu,
          renderHeader: settingsMenuColumn.renderHeader,
        },
        ...visibleColumns.slice(actionIdx + 1),
      ];
    } else {
      finalColumns = [...visibleColumns, settingsMenuColumn];
    }

    return finalColumns;
  }, [columnVisibility, columns, processedColumnSettings, settingsMenuColumn]);

  return (
    <Box>
      <WsPaginationBar
        totalRecords={totalRecords}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
      <Divider />
      <StyledDataGrid
        hideFooter
        columns={columnsData}
        rows={rows}
        page={page}
        pageSize={pageSize}
        {...restProps}
      />
      {totalRecords > 10 && (
        <>
          <Divider />
          <WsPaginationBar
            totalRecords={totalRecords}
            page={page}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}
    </Box>
  );
};

export default WsDataGrid;
